import moment from "moment";
import sessionHandling from "services/utils/notificationUtils";
import { apiErrorText } from "pages/PayRoll/PayInputs/ShiftEarnings/shiftEarningsApi";
import { baseForApi, joinBase } from "./salaryComponentData";

// Payroll > Salary > Salary Components on the payroll/salary/ APIs (spec
// section 4: components are versioned, no effective dates).
//
// The API doc has no sample responses, so every reader below accepts the
// likely field names and falls back to safe defaults. Check them against a
// live response.

const BASE = "payroll/salary/";
const COMPONENT = `${BASE}salary-component/`;
const query = (path, params) => {
  const search = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  ).toString();
  return search ? `${path}?${search}` : path;
};

// Same as the Pay Inputs request helper. The salary APIs answer an action
// the current state doesn't allow (a used component, ...) with 400 and a
// message, so 409 is only the signed-in-elsewhere logout.
const request = async (domain, path, { method = "GET", body, signal } = {}) => {
  const response = await fetch(`${domain.replace(/\/$/, "")}/${path}`, {
    method,
    signal,
    headers: { "Content-Type": "application/json", Authorization: `Token ${localStorage.getItem("token")}` },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  if (response.status === 204 && response.ok) return null;
  const data = await response.json().catch(() => null);
  const message = apiErrorText(data?.error || data?.detail || data?.message) || (response.ok ? "" : apiErrorText(data));
  if (response.status === 409) sessionHandling();
  if (!response.ok) throw new Error(message || `Request failed (${response.status}). Please try again.`);
  return data;
};

// ---------- Readers ----------

const unwrap = (data) => (data && !Array.isArray(data) && data.data !== undefined ? data.data : data);
const listOf = (data, keys = []) => {
  const value = unwrap(data);
  if (Array.isArray(value)) return value;
  for (const key of ["results", "rows", "items", ...keys]) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return null;
};
const first = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");
// The success text the server sends with a save ("Conveyance created as V1.").
const messageOf = (data) => (typeof data?.message === "string" ? data.message.trim() : "");
const toNumber = (value) => {
  const number = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(number) ? null : number;
};
const toBool = (value, fallback) => {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1 || value === "1" || value === "Yes") return true;
  if (value === "false" || value === 0 || value === "0" || value === "No") return false;
  return fallback;
};
const toDay = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};
const personName = (value) =>
  typeof value === "object" && value ? first(value.name, value.user_name, value.full_name, value.username) || "" : value || "";
const versionNumber = (value) => {
  const number = toNumber(String(value ?? "").replace(/^v/i, ""));
  return number == null ? null : number;
};

// One calculation (a component's current version or a version history row)
// in the shape the screen uses: amount / percentage / base / formula.
const toCalculation = (row, fallback = {}) => {
  // The choices API calls methods "computation_method", so that name is read too.
  const method =
    first(row.method, row.computation_method, row.calculation_method, row.method_name, fallback.method) || "Fixed Amount";
  const value = first(row.value, row.computation_value, row.amount, row.fixed_amount, row.percentage, row.percentage_value, fallback.value);
  return {
    category: normalizeCategory(first(row.category, row.category_name, fallback.category)),
    method,
    amount: method === "Fixed Amount" ? toNumber(value) ?? "" : "",
    percentage: method === "Percentage" ? toNumber(value) ?? "" : "",
    // One code or a list of codes (BASIC + DA), kept as "BASIC + DA".
    base: joinBase(first(row.percent_base_code, row.percentage_base_code, row.base_code, row.value_based_on, row.based_on, row.base, fallback.base)) || "GROSS",
    formula: method === "Formula" ? first(row.formula, fallback.formula) || "" : "",
    prorate: toBool(first(row.prorate, row.is_prorate), toBool(fallback.prorate, true)),
  };
};

// "EARNINGS", "earning", "additional_earnings", "Other Deduction" -> the
// screen's category names, so a row always lands in its tab / template section.
const CATEGORY_NAMES = ["Earnings", "Additional Earnings", "Deductions", "Additional Deductions", "Employer Contribution", "Reimbursement", "Calculation"];
export const normalizeCategory = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const key = text.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").replace(/s$/, "").replace(/^other /, "additional ");
  return CATEGORY_NAMES.find((name) => name.toLowerCase().replace(/s$/, "") === key) || text;
};

const SYSTEM_CATEGORIES = ["Additional Earnings", "Additional Deductions", "Employer Contribution"];

// API component -> the component object the screen and panels use. The
// version list, templates and employees are filled in when it's opened
// (componentDetails); until then the list row's own counts are used.
export const fromComponentApi = (row) => {
  const status = first(row.status, row.usage_status) || "";
  const category = normalizeCategory(first(row.category, row.category_name));
  const system =
    toBool(first(row.is_system, row.system), undefined) ?? (status === "System" || SYSTEM_CATEGORIES.includes(category));
  const active = toBool(first(row.is_active, row.active), undefined) ?? status !== "Inactive";
  const version =
    versionNumber(first(row.version_no, row.component_version_no, row.current_version, row.version, row.version_number)) ?? 1;
  return {
    id: first(row.id, row.component_id),
    name: first(row.name, row.component_name) || "",
    code: first(row.code, row.component_code) || "",
    description: row.description || "",
    payslip_name: row.payslip_name || "",
    category,
    show_in_payslip: toBool(row.show_in_payslip, true),
    system,
    statutory: toBool(row.is_statutory, false),
    // "Statutory" / "Pay Input" for system components added at run time.
    system_type: row.system_type || "",
    balancing: toBool(first(row.is_balancing, row.balancing), false),
    active,
    // Used / Unused / System / Inactive as the server works it out.
    status,
    template_count: toNumber(first(row.template_count, row.templates_count, row.used_in_templates, row.used_templates)),
    employee_count: toNumber(first(row.employee_count, row.employees_count, row.used_by_employees, row.used_employees)),
    deactivation: active
      ? null
      : {
          at: toDay(first(row.deactivated_at, row.deactivated_on, row.updated_at)),
          by: personName(first(row.deactivated_by, row.updated_by)),
          reason: first(row.deactivation_reason, row.inactive_reason, row.reason) || "",
        },
    versions: [
      {
        version,
        ...toCalculation(row, { category }),
        created_at: toDay(first(row.version_created_at, row.updated_at, row.created_at)),
        created_by: personName(first(row.version_created_by, row.updated_by, row.created_by)),
        note: first(row.change_note, row.note) || "",
      },
    ],
    templates: [],
    employees: [],
    activity: [],
  };
};

const fromVersionApi = (row, component) => ({
  version: versionNumber(first(row.version, row.version_number, row.version_no)) ?? 1,
  ...toCalculation(row, { category: component.category }),
  // Full timestamp: the Activity Log shows the time too.
  created_at: first(row.created_on, row.created_at) || "",
  created_by: personName(first(row.created_by_name, row.created_by)),
  note: first(row.change_note, row.note) || "",
  template_count: toNumber(first(row.used_in_templates, row.template_count)),
});

const fromTemplateApi = (row, index) => ({
  key: String(first(row.template_id, row.id, index)),
  name: first(row.template_name, row.name) || "",
  pay_basis: first(row.pay_basis, row.pay_frequency, row.salary_basis) || "",
  template_version: versionNumber(first(row.template_version, row.template_version_number)) ?? 1,
  uses_version: versionNumber(first(row.pinned_version, row.component_version, row.uses_version, row.version)) ?? 1,
  upgrade_available: toBool(row.upgrade_available, false),
  employees: toNumber(first(row.employee_count, row.employees_count, row.employees)) ?? 0,
  calc_override: first(row.calc_override, row.calculation_override) || null,
});

const fromEmployeeApi = (row, index) => {
  const basis = String(first(row.pay_basis, row.salary_basis) || "").toLowerCase();
  return {
    id: String(first(row.id, row.salary_id, `${row.employee_id}-${index}`)),
    name: first(row.employee_name, row.user_name, row.name) || "",
    employee_code: String(first(row.employee_code, row.employee_id, row.emp_code) ?? ""),
    department: first(row.department, row.department_name) || "",
    template: first(row.template_name, row.template) || "",
    customized: toBool(first(row.is_customized, row.customized), String(row.structure || "").toLowerCase() === "customized"),
    version: versionNumber(first(row.component_version, row.version, row.pinned_version)) ?? 1,
    calculation: first(row.calculation, row.calculation_text) || "",
    amount: toNumber(first(row.amount, row.value)),
    per: first(row.per) || (basis.includes("daily") ? "day" : basis.includes("weekly") ? "week" : "month"),
    effective_from: toDay(first(row.effective_from, row.effective_date)),
  };
};

// ---------- Component list / details ----------

// ---------- Finding component rows in an answer ----------

const CATEGORY_KEYS = {
  earnings: "Earnings",
  additional_earnings: "Additional Earnings",
  deductions: "Deductions",
  additional_deductions: "Additional Deductions",
  employer_contribution: "Employer Contribution",
  employer_contributions: "Employer Contribution",
  // The template APIs' keys for the stored Other Earnings / Other Deduction.
  other_earnings: "Additional Earnings",
  other_deductions: "Additional Deductions",
  reimbursements: "Reimbursement",
  calculations: "Calculation",
};
const categoryFromKey = (key) => CATEGORY_KEYS[String(key).trim().toLowerCase().replace(/[\s-]+/g, "_")];

// A component-like row: a code plus an id or a name.
const isComponentRow = (row) =>
  row && typeof row === "object" && !Array.isArray(row) &&
  first(row.code, row.component_code) != null && first(row.id, row.component_id, row.name, row.component_name) != null;

// Every component row in an answer, whatever it is wrapped or grouped in:
// a plain list, { data | results | components | list: [...] } next to the
// summary card counts, or per category ({ earnings: [...] } / { "Additional
// Earnings": [...] } / [{ category, components: [...] }]). A group's
// category fills in a row's missing category. Count objects are skipped.
export const collectComponentRows = (value, category, depth = 0) => {
  if (depth > 4 || value == null) return [];
  const withCategory = (row) => (category && !first(row.category, row.category_name) ? { ...row, category } : row);
  if (Array.isArray(value)) {
    return value.flatMap((item) =>
      isComponentRow(item)
        ? [withCategory(item)]
        : collectComponentRows(
            item?.components ?? item?.items ?? item?.rows ?? item?.results,
            first(item?.category, categoryFromKey(item?.label ?? ""), category),
            depth + 1,
          ),
    );
  }
  if (typeof value !== "object") return [];
  if (isComponentRow(value)) return [withCategory(value)];
  return Object.entries(value).flatMap(([key, item]) =>
    item && typeof item === "object" ? collectComponentRows(item, categoryFromKey(key) || category, depth + 1) : [],
  );
};

// Rows found once each (by id, else code), in answer order.
export const uniqueComponentRows = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = String(first(row.id, row.component_id, row.code, row.component_code));
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// The list. An answer with no component rows is an empty company; only a
// non-JSON / non-object answer is an error.
export const listSalaryComponents = async (domain, companyId, signal) => {
  const data = await request(domain, query(COMPONENT, { company_id: companyId }), { signal });
  if (!data || typeof data !== "object") throw new Error("The server returned an invalid salary component list.");
  return uniqueComponentRows(collectComponentRows(unwrap(data))).map(fromComponentApi);
};

const getSalaryComponent = async (domain, componentId, signal) => {
  const data = unwrap(await request(domain, query(COMPONENT, { component_id: componentId }), { signal }));
  const row = Array.isArray(data) ? data[0] : data?.component || data;
  return row && typeof row === "object" ? fromComponentApi(row) : null;
};

// Everything the view panel shows for one component: the component itself
// plus the Version History, Used by Templates and Used by Employees tabs.
// A tab whose call fails stays empty and its error is returned.
export const componentDetails = async (domain, component, signal) => {
  const id = component.id;
  const [single, versions, templates, employees] = await Promise.allSettled([
    getSalaryComponent(domain, id, signal),
    request(domain, query(`${BASE}component-versions/`, { component_id: id }), { signal }),
    request(domain, query(`${BASE}component-templates/`, { component_id: id }), { signal }),
    request(domain, query(`${BASE}component-employees/`, { component_id: id }), { signal }),
  ]);
  const base = single.status === "fulfilled" && single.value ? { ...component, ...single.value } : component;
  const versionRows = versions.status === "fulfilled" ? listOf(versions.value, ["versions"]) : null;
  const templateRows = templates.status === "fulfilled" ? listOf(templates.value, ["templates"]) : null;
  const employeeRows = employees.status === "fulfilled" ? listOf(employees.value, ["employees"]) : null;
  const errors = [single, versions, templates, employees]
    .filter((result) => result.status === "rejected" && result.reason?.name !== "AbortError")
    .map((result) => result.reason?.message);
  const history = versionRows?.length
    ? versionRows.map((row) => fromVersionApi(row, base)).sort((a, b) => a.version - b.version)
    : base.versions;
  // The newest version row may only carry its number and note: the
  // calculation comes from the component itself.
  const latest = history[history.length - 1];
  const own = base.versions[base.versions.length - 1];
  if (latest && own && latest.version === own.version) {
    history[history.length - 1] = { ...own, ...Object.fromEntries(Object.entries(latest).filter(([, value]) => value !== "" && value != null)) };
  }
  return {
    component: {
      ...base,
      versions: history,
      templates: templateRows ? templateRows.map(fromTemplateApi) : [],
      employees: employeeRows ? employeeRows.map(fromEmployeeApi) : [],
      details_loaded: true,
    },
    errors,
  };
};

// ---------- Save / status ----------

const calculationBody = (form) => ({
  category: form.category,
  method: form.method,
  ...(form.method === "Fixed Amount" ? { value: Number(form.amount) } : {}),
  ...(form.method === "Percentage" ? { value: Number(form.percentage), percent_base_code: baseForApi(form.base) } : {}),
  ...(form.method === "Formula" ? { formula: form.formula.trim() } : {}),
  // Prorate applies to Fixed Amount only; other methods send false.
  prorate: form.method === "Fixed Amount" && Boolean(form.prorate),
});

const infoBody = (form) => ({
  name: form.name.trim(),
  show_in_payslip: Boolean(form.show_in_payslip),
  payslip_name: form.payslip_name.trim(),
  description: form.description.trim(),
});

// POST: creates the component as version 1. Returns its id when the answer
// carries one.
export const createSalaryComponent = async (domain, companyId, form, signal) => {
  const data = await request(domain, COMPONENT, {
    method: "POST",
    body: { company_id: Number(companyId), ...infoBody(form), code: form.code.trim().toUpperCase(), ...calculationBody(form) },
    signal,
  });
  const row = unwrap(data);
  return { id: first(row?.id, row?.component_id, row?.component?.id) ?? null, message: messageOf(data) };
};

// PUT: name / payslip / description change in place. Calculation changes
// change in place on an Unused component and save the next version on a
// Used one (server rule); change_note is sent with those. System components
// save the same way as user ones.
// Each save resolves to the server's message for the toast.
export const updateSalaryComponent = async (domain, id, form, { newVersion }, signal) =>
  messageOf(
    await request(domain, COMPONENT, {
      method: "PUT",
      body: { id, ...infoBody(form), ...calculationBody(form), ...(newVersion ? { change_note: form.note.trim() } : {}) },
      signal,
    }),
  );

export const deactivateSalaryComponent = async (domain, id, reason, signal) =>
  messageOf(await request(domain, COMPONENT, { method: "PUT", body: { id, status: "Inactive", reason }, signal }));

export const activateSalaryComponent = async (domain, id, signal) =>
  messageOf(await request(domain, COMPONENT, { method: "PUT", body: { id, status: "Active" }, signal }));

// Soft delete: Unused, non-system components only (a Used one answers 400).
export const deleteSalaryComponent = async (domain, id, signal) =>
  messageOf(await request(domain, COMPONENT, { method: "PUT", body: { id, is_deleted: true }, signal }));

// ---------- Checks ----------

// Name / code checks: null when fine, else the message to show. A 400 is
// read as "not allowed" with the server's text.
const checkResult = async (call, fallback) => {
  let data;
  try {
    data = unwrap(await call());
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    return error.message || fallback;
  }
  if (typeof data === "boolean") return data ? null : fallback;
  const taken =
    [data?.exists, data?.is_exists, data?.is_exist, data?.is_duplicate, data?.duplicate].some((value) => value === true) ||
    [data?.available, data?.is_available, data?.valid, data?.is_valid, data?.status].some((value) => value === false);
  return taken ? apiErrorText(data?.message || data?.error) || fallback : null;
};

export const checkComponentName = (domain, companyId, name, componentId, signal) =>
  checkResult(
    () => request(domain, query(`${BASE}check-component-name/`, { company_id: companyId, component_name: name.trim(), component_id: componentId }), { signal }),
    "A component with this name already exists",
  );

export const checkComponentCode = (domain, companyId, code, signal) =>
  checkResult(
    () => request(domain, query(`${BASE}check-component-code/`, { company_id: companyId, code: code.trim().toUpperCase() }), { signal }),
    "This code can't be used",
  );

// Formula editor Test box: checks the formula the way a save would and runs
// it at the sample gross. Returns { valid, amount, message }; amount is
// null when the answer has no result. A 400 is an invalid formula.
export const validateComponentValue = async (domain, companyId, { category, formula, gross }, signal) => {
  let data;
  try {
    data = unwrap(
      await request(domain, query(`${BASE}validate-component-value/`, { company_id: companyId, category, formula, gross }), { signal }),
    );
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    return { valid: false, amount: null, message: error.message };
  }
  const valid = toBool(first(data?.valid, data?.is_valid), !data?.error);
  return {
    valid,
    amount: toNumber(first(data?.result, data?.value, data?.amount, data?.computed_value, data?.output)),
    message: apiErrorText(data?.message || data?.error) || "",
  };
};

// ---------- Dropdowns ----------

const optionValue = (item) =>
  typeof item === "string" ? item : first(item?.value, item?.code, item?.name, item?.label, item?.key);

// Form choices from get-component-choices, as plain names (null when the
// answer doesn't have that list):
//   categories: what a user component can be created as, from
//     category_choices_by_type (component_type "User"), else category_choices
//   methods: computation_method_choices (Fixed Amount / Percentage / Formula)
//   statusCards: list_status_cards (All / Used / Unused / System / Inactive)
// Every choice is { label }.
export const getComponentChoices = async (domain, signal) => {
  const data = unwrap(await request(domain, `${BASE}get-component-choices/`, { signal }));
  const labels = (value) => (Array.isArray(value) ? value.map(optionValue).filter(Boolean) : null);
  const userType = Array.isArray(data?.category_choices_by_type)
    ? data.category_choices_by_type.find((item) => item?.component_type === "User" && !item?.system_type)
    : null;
  return {
    categories: (labels(userType?.categories) || labels(data?.category_choices))?.map(normalizeCategory) ?? null,
    methods: labels(data?.computation_method_choices),
    statusCards: labels(data?.list_status_cards),
  };
};

// Percentage "Of" choices: GROSS plus the company's active Earnings codes,
// without the component being edited.
// Each choice must end up as a component CODE (it's sent as
// percent_base_code and the preview looks values up by code), never an id:
// code-like fields first, a non-numeric value, then a code in the label
// ("Basic Salary (BASIC)" or "BASIC - Basic Salary").
const baseCode = (item) => {
  if (typeof item === "string") return item;
  const direct = first(item?.code, item?.component_code, item?.percent_base_code, item?.base_code);
  if (direct) return String(direct);
  if (item?.value != null && !/^\d+$/.test(String(item.value))) return String(item.value);
  const label = String(first(item?.label, item?.name) || "");
  const inBrackets = label.match(/\(([A-Z][A-Z0-9_]*)\)\s*$/);
  if (inBrackets) return inBrackets[1];
  const leading = label.match(/^([A-Z][A-Z0-9_]*)\s*[-–:]/);
  if (leading) return leading[1];
  return /^[A-Z][A-Z0-9_]*$/.test(label) ? label : null;
};

export const getPercentBases = async (domain, companyId, componentId, signal) => {
  const data = await request(domain, query(`${BASE}get-values-basedon/`, { company_id: companyId, component_id: componentId }), { signal });
  const list = listOf(data, ["values", "codes", "bases", "options", "choices"]);
  const codes = list ? [...new Set(list.map(baseCode).filter(Boolean))] : [];
  // Nothing readable: the form falls back to GROSS + the company's earnings.
  return codes.length ? codes : null;
};
